import { v4 as uuidv4 } from 'uuid';

// Time tracked analytics: a flow is one visit to Time tracked. Every tab shares the current flow.
// A session is one visit to Hubstaff. Every tab shares it, and the server reads it to track approvals.
const FLOW_STARTED_EVENT = 'Timesheets flow started';
const MENU_OPENED_EVENT = 'Actions menu opened';
const BADGE_CLICKED_EVENT = 'Sidebar badge clicked';
const BADGE_LANDED_EMPTY_EVENT = 'Sidebar badge landed empty';
const PREVIOUS_PAGE_KEY = 'time_tracked_previous_page';
const SIDEBAR_CLICK_KEY = 'time_tracked_sidebar_click';
const SIDEBAR_CLICK_TTL = 30 * 1000;
const SESSION_COOKIE = 'analytics_session_id';
const SESSION_COOKIE_PATTERN = new RegExp(`(?:^|; )${SESSION_COOKIE}=([^;]+)`);
const SESSION_MAX_AGE = 30 * 60;
const SESSION_REFRESH_INTERVAL = 60 * 1000;
const ENTRY_MENU_SELECTOR = '[data-analytics-menu="entry_row"], .time-entry-row .table-actions-dropdown';
const BADGE_TYPES = { edit_approve: 'approvals', manual_time_requests: 'manual_time' };
const LANDING_LIST_SELECTOR = '#timesheets-table, #manual-time-requests-table';
const LANDING_FILTERS_SELECTOR = '#timesheets_list_form, #manual_time_requests_list_form';
const LANDING_FILTER_FIELDS = { members: 'users', teams: 'teams' };

// [path, landing_tab, landing_page]. Timesheets is a summary in the new IA and View & edit in the legacy IA.
const TIME_TRACKED_PAGES = [
  [/^\/organizations\/\d+\/time_entries(\/(daily|weekly|period|calendar))?$/, 'timesheets', null],
  [/^\/organizations\/\d+\/time_entries\/member_panels$/, 'timesheets', 'view_and_edit'],
  [/^\/organizations\/\d+\/timesheets$/, 'approvals', 'index'],
  [/^\/organizations\/\d+\/timesheets_panels$/, 'approvals', 'view_and_edit'],
  [/^\/timesheets\/\d+$/, 'approvals', 'view_and_edit'],
  [/^\/organizations\/\d+\/manual_time_requests$/, 'manual_time_requests', 'index'],
];

let dialogRoute = 'other';
let sessionRefreshedAt = 0;

const newIa = () => document.body.dataset.batchManualTimeEntriesTimesheetsEnabled === 'true';

const userId = () => JSON.parse(document.body.dataset.currentUserDetails).id;

const flowKey = () => `time_tracked_flow_org_${document.body.dataset.organizationId}_user_${userId()}`;

function timeTrackedPage() {
  const page = TIME_TRACKED_PAGES.find(([path]) => path.test(window.location.pathname));

  if (!page) {
    return null;
  }

  const [, landingTab, landingPage] = page;

  return { landing_tab: landingTab, landing_page: landingPage ?? (newIa() ? 'index' : 'view_and_edit') };
}

function track(event, properties) {
  window.analytics?.track(event, {
    user_id: userId(),
    user_role: document.body.dataset.userRole,
    new_ia: newIa(),
    ...properties,
  });
}

// The cookie expires after 30 minutes without activity. The browser sends it with every request.
function refreshSession() {
  const sessionId = document.cookie.match(SESSION_COOKIE_PATTERN)?.[1] ?? uuidv4();

  document.cookie = `${SESSION_COOKIE}=${sessionId}; path=/; max-age=${SESSION_MAX_AGE}; samesite=lax`;
  sessionRefreshedAt = Date.now();

  return sessionId;
}

function recordActivity() {
  try {
    if (Date.now() - sessionRefreshedAt > SESSION_REFRESH_INTERVAL) {
      refreshSession();
    }
  } catch {
    // Analytics must never break the page.
  }
}

function entryPoint(pageshowEvent, sidebarClick) {
  if (pageshowEvent.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
    return 'back_button';
  }

  return sidebarClick?.entryPoint ?? 'deep_link';
}

// The destination page sends the badge events, because a page unload can cancel a request sent at click time.
function trackBadgeLanding(badge, sessionId) {
  track(BADGE_CLICKED_EVENT, { session_id: sessionId, ...badge });

  const list = document.querySelector(LANDING_LIST_SELECTOR);

  if (list && !list.querySelector('tr[data-row-id]')) {
    const filters = document.querySelector(LANDING_FILTERS_SELECTOR)?.elements ?? {};
    const filterValue = (name) => filters[name]?.value || filters[`${name}[selection]`]?.value;

    track(BADGE_LANDED_EMPTY_EVENT, {
      session_id: sessionId,
      badge_type: badge.badge_type,
      count_at_click: badge.count,
      active_filters: Object.keys(LANDING_FILTER_FIELDS).filter((filter) => filterValue(LANDING_FILTER_FIELDS[filter])),
      date_range: filters.date?.value,
    });
  }
}

// `pageshow` also fires when the browser restores a page from the back/forward cache, where scripts do not run again.
function registerVisit(pageshowEvent) {
  try {
    const sessionId = refreshSession();
    const page = timeTrackedPage();
    const entered = page && sessionStorage.getItem(PREVIOUS_PAGE_KEY) !== 'true';
    const storedClick = JSON.parse(sessionStorage.getItem(SIDEBAR_CLICK_KEY));
    const sidebarClick = storedClick && Date.now() - storedClick.at < SIDEBAR_CLICK_TTL ? storedClick : null;

    sessionStorage.setItem(PREVIOUS_PAGE_KEY, String(Boolean(page)));
    sessionStorage.removeItem(SIDEBAR_CLICK_KEY);

    if (entered) {
      const flowId = uuidv4();
      const entry = entryPoint(pageshowEvent, sidebarClick);

      localStorage.setItem(flowKey(), flowId);
      track(FLOW_STARTED_EVENT, {
        flow_id: flowId,
        entry_point: entry,
        // Sidebar and badge entries both carry the alert count from the clicked item; other entries have no click context.
        alert_count: entry === 'sidebar' || entry === 'badge' ? (sidebarClick?.alertCount ?? 0) : 0,
        ...page,
      });
    }

    if (sidebarClick?.badge) {
      trackBadgeLanding(sidebarClick.badge, sessionId);
    }
  } catch {
    // Analytics must never break the page.
  }
}

const badgeVisible = (badge) => Boolean(badge) && badge.style.display !== 'none';

function badgeSnapshot(badge) {
  const badgeType = BADGE_TYPES[badge.dataset.pendingActionKey];
  const bothVisible = Object.keys(BADGE_TYPES)
    .every((key) => badgeVisible(document.querySelector(`[data-pending-action-key="${key}"]`)));

  return badgeType && {
    badge_type: badgeType,
    count: Number(badge.dataset.pendingActionCount),
    badges_present: bothVisible ? 'both' : badgeType,
  };
}

function recordSidebarClick(event) {
  const anchor = event.target.closest('a[role="menuitem"]');
  const item = anchor?.closest('[role="menu-item"], [role="sub-menu-item"]');

  if (!item || event.metaKey || event.ctrlKey || event.shiftKey) {
    return;
  }

  const badge = item.querySelector(':scope > [data-pending-action-key]');
  const badgeClicked = badgeVisible(badge);
  // The clicked item's badge sits inside the anchor for a parent and is a direct child of the row for a sub-item.
  const alertBadge = anchor.querySelector('[data-pending-action-key]') ?? badge;

  try {
    sessionStorage.setItem(SIDEBAR_CLICK_KEY, JSON.stringify({
      entryPoint: badgeClicked ? 'badge' : 'sidebar',
      badge: badgeClicked ? badgeSnapshot(badge) : undefined,
      alertCount: badgeVisible(alertBadge) ? Number(alertBadge.dataset.pendingActionCount) || 0 : 0,
      at: Date.now(),
    }));
  } catch {
    // Analytics must never break the page.
  }
}

// The route is set by the click that opens a time entry dialog. Clicks while a dialog is open keep it.
function recordDialogRoute(event) {
  if (!document.querySelector('.app-dialog.opened')) {
    dialogRoute = event.target.closest(ENTRY_MENU_SELECTOR) ? 'actions_menu' : 'other';
  }
}

export function currentFlowId() {
  try {
    return timeTrackedPage() ? localStorage.getItem(flowKey()) : null;
  } catch {
    return null;
  }
}

export function timeEntryAnalyticsParams() {
  return { flow_id: currentFlowId(), route: dialogRoute };
}

function trackMenuOpened(context) {
  const flowId = currentFlowId();

  if (context && flowId) {
    track(MENU_OPENED_EVENT, { flow_id: flowId, context });
  }
}

export function startAnalyticsVisit() {
  window.addEventListener('pageshow', registerVisit);
  document.addEventListener('click', recordActivity, true);
  document.addEventListener('keydown', recordActivity, true);
  document.addEventListener('click', recordSidebarClick, true);
  document.addEventListener('click', recordDialogRoute, true);
  document.addEventListener('dropdown:opened', (event) => trackMenuOpened(event.target.dataset.analyticsMenu));
  document.addEventListener('shown.bs.dropdown', (event) => {
    if (event.target.closest('.time-entry-row')) {
      trackMenuOpened('entry_row');
    }
  });
}
